import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// Math symbols exactly as shown in screenshot
const MATH_SYMBOLS = [
  { label: 'x²', val: 'x²' }, { label: '√', val: '√' }, { label: '∫', val: '∫' },
  { label: 'Σ', val: 'Σ' }, { label: 'π', val: 'π' }, { label: '∞', val: '∞' },
  { label: '<', val: '<' }, { label: '>', val: '>' }, { label: '∂', val: '∂' },
  { label: '∇', val: '∇' }, { label: 'θ', val: 'θ' }, { label: 'λ', val: 'λ' },
  { label: 'μ', val: 'μ' }, { label: 'α', val: 'α' }, { label: 'β', val: 'β' },
  { label: 'ω', val: 'ω' }, { label: 'Δ', val: 'Δ' }, { label: 'ρ', val: 'ρ' },
];

// Example chips exactly as shown in screenshot
const EXAMPLE_CHIPS = [
  { emoji: '⚡', text: "Ohm's law: 12V, 3Ω circuit" },
  { emoji: '🌊', text: 'Wave speed: 60Hz, 5m wavelength' },
  { emoji: '🔮', text: 'Projectile motion: 45° angle, 20m/s' },
  { emoji: '⚛️', text: 'Bohr model hydrogen energy levels' },
  { emoji: '🔥', text: 'Ideal gas law calculation' },
];

const FEATURES = [
  { icon: '⚡', color: '#fff3e0', title: 'Instant Solutions', desc: 'Get step-by-step solutions in seconds for any physics problem.' },
  { icon: '📸', color: '#e8f5e9', title: 'Image Recognition', desc: 'Upload photos of textbook problems, diagrams, or handwritten notes.' },
  { icon: '🧮', color: '#e3f2fd', title: 'Advanced Math', desc: 'Full support for complex equations and formulas with math symbols.' },
  { icon: '📚', color: '#fce4ec', title: 'All Topics Covered', desc: 'Mechanics, thermodynamics, quantum physics, electromagnetism and more.' },
  { icon: '🎯', color: '#f3e5f5', title: 'Exam Preparation', desc: 'Practice unlimited problems and master every physics concept.' },
  { icon: '🌍', color: '#e0f2f1', title: '24/7 Available', desc: 'Study anytime, anywhere. Your personal AI physics tutor never sleeps.' },
];

const TOPICS = [
  { icon: '⚙️', name: 'Mechanics' }, { icon: '🔥', name: 'Thermodynamics' },
  { icon: '⚡', name: 'Electromagnetism' }, { icon: '🌊', name: 'Waves & Optics' },
  { icon: '⚛️', name: 'Quantum Physics' }, { icon: '🪐', name: 'Relativity' },
  { icon: '🔬', name: 'Nuclear Physics' }, { icon: '🧲', name: 'Magnetism' },
  { icon: '💧', name: 'Fluid Mechanics' }, { icon: '🌡️', name: 'Heat Transfer' },
  { icon: '🔭', name: 'Astrophysics' }, { icon: '📡', name: 'Electronics' },
];

const TESTIMONIALS = [
  { name: 'Rahul Sharma', role: 'IIT JEE Student', text: 'PhysicsAI helped me crack JEE! The step-by-step explanations are incredible.', init: 'R' },
  { name: 'Dr. Priya Mehta', role: 'Physics Professor', text: 'I recommend this to all my students. Explains concepts better than many textbooks.', init: 'P' },
  { name: 'Alex Johnson', role: 'Engineering Student', text: 'Saved hours on homework. From kinematics to quantum physics, handles everything.', init: 'A' },
  { name: 'Emma Wilson', role: 'High School Student', text: 'Finally a tool that explains WHY, not just gives the answer. Grades went from C to A.', init: 'E' },
  { name: 'Dr. Sanjay Patel', role: 'Research Physicist', text: 'Surprisingly accurate for complex computations. Essential in my research workflow.', init: 'S' },
  { name: 'Maria Garcia', role: 'College Student', text: 'Like having a genius tutor available 24/7. Image upload feature is a game-changer.', init: 'M' },
];

export default function HomePage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [freeLeft, setFreeLeft] = useState(5);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Load free questions used from sessionStorage
  useEffect(() => {
    const used = parseInt(sessionStorage.getItem('freeQUsed') || '0');
    setFreeLeft(Math.max(0, 5 - used));
  }, []);

  const insertSymbol = (sym) => {
    const ta = textareaRef.current;
    if (!ta) { setInput(prev => prev + sym); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newVal = input.substring(0, start) + sym + input.substring(end);
    setInput(newVal);
    setTimeout(() => {
      ta.selectionStart = ta.selectionEnd = start + sym.length;
      ta.focus();
    }, 0);
  };

  const handleSend = async (overrideText) => {
    const question = (overrideText !== undefined ? overrideText : input).trim();
    if (!question && !image) return;
    if (!user && freeLeft <= 0) {
      setMessages(prev => [...prev, {
        role: 'ai',
        content: '🔒 You have used all your free questions for today. Please sign up for a free account to get more queries!'
      }]);
      return;
    }
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: question, image: imagePreview }]);
    const imgData = image;
    setImage(null); setImagePreview(null);
    setLoading(true);

    if (!user) {
      const used = parseInt(sessionStorage.getItem('freeQUsed') || '0') + 1;
      sessionStorage.setItem('freeQUsed', used);
      setFreeLeft(Math.max(0, 5 - used));
    }

    try {
      const data = await api.chat(question, imgData);
      if (data.error) {
        setMessages(prev => [...prev, {
          role: 'ai',
          content: data.upgrade
            ? '🔒 Query limit reached. Please upgrade your plan to continue.'
            : data.signup
              ? '👋 Daily guest limit reached. Sign up free for more queries!'
              : `Error: ${data.error}`
        }]);
      } else {
        setMessages(prev => [...prev, { role: 'ai', content: data.answer }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'ai', content: 'Sorry, something went wrong. Please try again.' }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setImage(ev.target.result); setImagePreview(ev.target.result); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const queriesUsed = user?.queries_used || 0;
  const queriesLimit = user?.queries_limit || 10;

  return (
    <div>
      <Navbar user={user} />

      {/* Hero */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-badge">⚛️ AI-Powered Physics Tutor</div>
          <h1>Solve Any <span>Physics Problem</span><br />Instantly with AI</h1>
          <p>Get step-by-step solutions, detailed explanations and conceptual clarity for all physics topics — from mechanics to quantum physics.</p>
          <div className="hero-cta">
            <a href="#chat" className="btn-primary" style={{ fontSize: 16, padding: '14px 32px' }}>Try Free Now →</a>
            <Link to="/pricing" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', padding: '12px 28px', borderRadius: 8, border: '2px solid rgba(255,255,255,0.5)', textDecoration: 'none', fontWeight: 600, fontSize: 15 }}>View Pricing</Link>
          </div>
          <div className="hero-stats">
            <div className="hero-stat"><div className="num">1M+</div><div className="label">Problems Solved</div></div>
            <div className="hero-stat"><div className="num">500K+</div><div className="label">Students Helped</div></div>
            <div className="hero-stat"><div className="num">99.9%</div><div className="label">Accuracy Rate</div></div>
            <div className="hero-stat"><div className="num">24/7</div><div className="label">Available</div></div>
          </div>
        </div>
      </section>

      {/* ===== EXACT CHAT TOOL MATCHING SCREENSHOT ===== */}
      <section className="chat-section" id="chat">
        <div className="chat-container">
          <div className="chat-box" style={{ borderRadius: 16, boxShadow: '0 4px 32px rgba(0,0,0,0.10)', border: '1px solid #e5e7eb', background: 'white', overflow: 'hidden', maxWidth: 780, margin: '0 auto' }}>

            {/* Messages / Welcome area */}
            <div className="chat-messages" style={{ minHeight: 340, maxHeight: 480, overflowY: 'auto', padding: '28px 24px 12px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {messages.length === 0 ? (
                /* Welcome state — exactly as screenshot */
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  {/* AI avatar — dark circle with atom icon */}
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <span style={{ fontSize: 18 }}>⚛️</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ background: '#f8f9ff', borderRadius: 14, borderTopLeftRadius: 4, padding: '18px 22px 14px', border: '1px solid #e5e7eb' }}>
                      <p style={{ textAlign: 'center', fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a2e' }}>
                        👋 Hello! I'm PhysicsTutor AI
                      </p>
                      <p style={{ textAlign: 'center', fontSize: 14.5, color: '#374151', lineHeight: 1.65, marginBottom: 12 }}>
                        I can solve any physics problem with step-by-step explanations. Try typing a problem or upload an image!
                      </p>
                      <p style={{ textAlign: 'center', fontSize: 13.5, color: '#6b7280', marginBottom: 16 }}>
                        🌿 I cover: Mechanics · Thermodynamics · Electromagnetism · Optics · Waves · Quantum Physics · Modern Physics
                      </p>
                      {/* Example chips — exactly matching screenshot */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-start' }}>
                        {EXAMPLE_CHIPS.map((chip, i) => (
                          <button key={i} onClick={() => handleSend(chip.text)} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                            background: 'white', border: '1.5px solid #e5e7eb', borderRadius: 8,
                            fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#374151',
                            transition: 'all 0.15s', fontFamily: 'inherit'
                          }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff6b00'; e.currentTarget.style.color = '#ff6b00'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
                          >
                            {chip.emoji} {chip.text}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div key={i} className={`msg ${msg.role}`} style={{ display: 'flex', gap: 12, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-start' }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: msg.role === 'user' ? 'linear-gradient(135deg,#ff6b00,#ff8c3a)' : '#1a1a2e',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 700, fontSize: msg.role === 'user' ? 15 : 16
                    }}>
                      {msg.role === 'user' ? (user?.name?.[0] || 'U') : '⚛️'}
                    </div>
                    <div style={{
                      maxWidth: '78%', padding: '12px 16px', borderRadius: 14,
                      borderTopLeftRadius: msg.role === 'ai' ? 4 : 14,
                      borderTopRightRadius: msg.role === 'user' ? 4 : 14,
                      background: msg.role === 'user' ? 'linear-gradient(135deg,#ff6b00,#ff8c3a)' : '#f8f9ff',
                      border: msg.role === 'ai' ? '1px solid #e5e7eb' : 'none',
                      color: msg.role === 'user' ? 'white' : '#1a1a2e',
                      fontSize: 14.5, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                    }}>
                      {msg.image && <img src={msg.image} alt="uploaded" style={{ maxWidth: 200, borderRadius: 8, marginBottom: 8, display: 'block' }} />}
                      {msg.content}
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>⚛️</div>
                  <div style={{ background: '#f8f9ff', border: '1px solid #e5e7eb', borderRadius: 14, borderTopLeftRadius: 4, padding: '14px 18px', display: 'flex', gap: 5, alignItems: 'center' }}>
                    {[0, 1, 2].map(j => (
                      <span key={j} style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff6b00', display: 'inline-block', animation: 'bounce 1.2s infinite', animationDelay: `${j * 0.2}s` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Image preview */}
            {imagePreview && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 16px 8px', background: '#f8f9ff', padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <img src={imagePreview} alt="preview" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                <span style={{ fontSize: 13, color: '#6b7280', flex: 1 }}>Image attached</span>
                <button onClick={() => { setImage(null); setImagePreview(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#ef4444', lineHeight: 1 }}>×</button>
              </div>
            )}

            {/* Math Symbol Toolbar — exactly matching screenshot */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 2, padding: '10px 14px 6px',
              borderTop: '1px solid #f3f4f6', background: 'white'
            }}>
              {MATH_SYMBOLS.map((s, i) => (
                <button key={i} onClick={() => insertSymbol(s.val)} style={{
                  minWidth: 34, height: 32, padding: '0 6px',
                  border: '1px solid #e5e7eb', borderRadius: 6,
                  background: 'white', cursor: 'pointer', fontSize: 13.5,
                  fontFamily: 'serif', color: '#374151', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s'
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fff3e0'; e.currentTarget.style.borderColor = '#ff6b00'; e.currentTarget.style.color = '#ff6b00'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
                  title={s.val}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Input row — exactly matching screenshot */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 14px 10px', background: 'white' }}>
              {/* Attachment button */}
              <button onClick={() => fileInputRef.current?.click()} style={{
                width: 40, height: 40, borderRadius: 10, border: '1.5px solid #e5e7eb',
                background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 18, flexShrink: 0, transition: 'all 0.15s'
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff6b00'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                title="Upload image"
              >📎</button>

              {/* Text input */}
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your physics question here..."
                rows={1}
                style={{
                  flex: 1, resize: 'none', border: '1.5px solid #e5e7eb', borderRadius: 10,
                  padding: '10px 14px', fontSize: 14.5, fontFamily: 'inherit', outline: 'none',
                  minHeight: 42, maxHeight: 110, lineHeight: 1.5, transition: 'border-color 0.2s'
                }}
                onFocus={e => e.target.style.borderColor = '#ff6b00'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 110) + 'px';
                }}
              />

              {/* Send button — orange, exactly as screenshot */}
              <button onClick={() => handleSend()} disabled={loading || (!input.trim() && !image)} style={{
                width: 44, height: 44, borderRadius: 10,
                background: (loading || (!input.trim() && !image)) ? '#ffb380' : 'linear-gradient(135deg,#ff6b00,#ff8c3a)',
                border: 'none', cursor: (loading || (!input.trim() && !image)) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 18, flexShrink: 0, transition: 'all 0.2s',
                boxShadow: (loading || (!input.trim() && !image)) ? 'none' : '0 3px 12px rgba(255,107,0,0.35)'
              }}>
                {loading ? '⏳' : '➤'}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleImageUpload} />
            </div>

            {/* Footer bar — "PhysicsTutor AI can make mistakes · 5 free questions today" */}
            <div style={{
              padding: '7px 16px 10px', background: 'white',
              borderTop: '1px solid #f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontSize: 12.5, color: '#9ca3af'
            }}>
              <span>PhysicsTutor AI can make mistakes. Always verify important results.</span>
              <span style={{ color: '#e5e7eb' }}>·</span>
              {user ? (
                <span style={{ color: '#ff6b00', fontWeight: 600 }}>
                  {queriesLimit === -1 ? 'Unlimited' : `${Math.max(0, queriesLimit - queriesUsed)} queries left`}
                </span>
              ) : (
                <span style={{ color: '#ff6b00', fontWeight: 600 }}>
                  {freeLeft > 0 ? (
                    <Link to="/register" style={{ color: '#ff6b00', textDecoration: 'none', fontWeight: 600 }}>
                      {freeLeft} free questions today
                    </Link>
                  ) : (
                    <Link to="/register" style={{ color: '#ef4444', textDecoration: 'none', fontWeight: 600 }}>
                      Sign up for more →
                    </Link>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features">
        <div className="section-header">
          <h2>Why Students Love PhysicsAI</h2>
          <p>Advanced AI technology meets expert physics knowledge for unmatched learning</p>
        </div>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon" style={{ background: f.color }}>{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Topics */}
      <section className="topics" id="topics">
        <div className="section-header">
          <h2>Physics Topics We Cover</h2>
          <p>From high school to university level — all physics branches covered</p>
        </div>
        <div className="topics-grid">
          {TOPICS.map((t, i) => (
            <div key={i} className="topic-card" onClick={() => { document.getElementById('chat')?.scrollIntoView({ behavior: 'smooth' }); setTimeout(() => handleSend(`Explain ${t.name} with examples`), 500); }}>
              <div className="ti">{t.icon}</div>
              <h4>{t.name}</h4>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-header">
          <h2>How PhysicsAI Works</h2>
          <p>Three simple steps to get your physics problem solved</p>
        </div>
        <div className="steps">
          {[
            { n: 1, icon: '✍️', title: 'Enter Your Problem', desc: 'Type your question or upload a photo — even handwritten notes work' },
            { n: 2, icon: '🤖', title: 'AI Analyzes', desc: 'Our AI identifies the physics concepts and applies the right formulas' },
            { n: 3, icon: '📝', title: 'Get Solution', desc: 'Receive detailed step-by-step explanation with all working shown' },
          ].map(s => (
            <div key={s.n} className="step">
              <div className="step-num">{s.n}</div>
              <div style={{ fontSize: 36, marginBottom: 12 }}>{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="section-header">
          <h2>What Students Say</h2>
          <p>Join over 500,000 students who improved their physics with AI</p>
        </div>
        <div className="testimonials-grid">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="testimonial-card">
              <div className="t-stars">★★★★★</div>
              <p className="t-text">"{t.text}"</p>
              <div className="t-author">
                <div className="t-avatar">{t.init}</div>
                <div><div className="t-name">{t.name}</div><div className="t-role">{t.role}</div></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg,#ff6b00,#ff8c3a)', padding: '80px 5%', textAlign: 'center', color: 'white' }}>
        <h2 style={{ fontSize: 40, fontWeight: 800, marginBottom: 16 }}>Ready to Master Physics?</h2>
        <p style={{ fontSize: 18, opacity: 0.9, marginBottom: 32 }}>Start solving physics problems for free today. No credit card required.</p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/register" style={{ background: 'white', color: '#ff6b00', padding: '14px 36px', borderRadius: 10, fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>Start Free Trial</Link>
          <Link to="/pricing" style={{ border: '2px solid rgba(255,255,255,0.5)', color: 'white', padding: '12px 32px', borderRadius: 10, fontWeight: 600, fontSize: 16, textDecoration: 'none' }}>View Plans</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export function Navbar({ user }) {
  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <Link to="/" className="navbar-logo">
          <div className="logo-icon">φ</div>
          <span className="logo-text">Physics<span>Tutor AI</span></span>
        </Link>
        <div className="navbar-links">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/pricing" className="nav-link">Pricing</Link>
          <a href="#topics" className="nav-link">About</a>
          {user ? (
            <span className="nav-link" style={{ color: 'var(--primary)', fontWeight: 600 }}>👤 {user.name?.split(' ')[0]}</span>
          ) : (
            <>
              <Link to="/login" className="nav-link">Sign In</Link>
              <Link to="/register" className="btn-primary">Get Started Free</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="footer-grid">
        <div className="footer-brand">
          <div style={{ width: 44, height: 44, background: 'linear-gradient(135deg,#ff6b00,#ff8c3a)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 20 }}>φ</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'white', margin: '10px 0 8px' }}>PhysicsTutor AI</div>
          <p>The most advanced AI physics tutor. Get instant step-by-step solutions for any physics problem.</p>
        </div>
        <div className="footer-col">
          <h4>Tools</h4>
          <Link to="/">Physics AI Solver</Link>
          <a href="#topics">Topics</a>
          <a href="#how-it-works">How it Works</a>
        </div>
        <div className="footer-col">
          <h4>Company</h4>
          <Link to="/pricing">Pricing</Link>
          <a href="#">About</a>
          <a href="#">Contact</a>
          <a href="#">Blog</a>
        </div>
        <div className="footer-col">
          <h4>Legal</h4>
          <a href="#">Privacy Policy</a>
          <a href="#">Terms of Service</a>
          <a href="#">Refund Policy</a>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© 2025 PhysicsTutor AI. All rights reserved.</p>
        <p>Powered by OpenAI GPT-4o</p>
      </div>
    </footer>
  );
}
